import { ComboBox } from '../../ui/comboBox/ComboBox';
import { handleKeyDownYearMonth } from 'src/utils/helpers';
import Input from 'src/components/forms/input';
import { RadioButton } from '../../../ui/radioButton';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    formatPostalCode_CA,
    formatPostalCode_US,
} from 'src/utils/formatInputs';
import React, { useEffect, useState } from 'react';
import './style.scss';

export interface IResidenceForm {
    values: any;
    errors: any;
    touched: any;
    handleChange: any;
    handleBlur: any;
    state: string;
    setState: any;
    typeOfResidence: string;
    setTypeOfResidence: any;
    submitErrors: any;
    setSubmitErrors: any;
    stateMistake: boolean;
    previousCountry: string;
    setPreviousCountry: any;
    previousState: string;
    setPreviousState: any;
    previousTypeOfResidence: string;
    setPreviousTypeOfResidence: any;
    previousStateMistake: boolean;
    isDisabled?: boolean;
    isCoaplicant?: boolean;
    initialState?: any;
    initialPrevState?: any;
    initialPrevCountry?: any;
    initialYearsAtAddress?: any;
}

const ResidenceForm: React.FC<IResidenceForm> = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    state,
    setState,
    typeOfResidence,
    setTypeOfResidence,
    submitErrors,
    setSubmitErrors,
    stateMistake,
    previousCountry,
    setPreviousCountry,
    previousState,
    setPreviousState,
    previousTypeOfResidence,
    setPreviousTypeOfResidence,
    previousStateMistake,
    isDisabled = false,
    isCoaplicant = false,
    initialState = null,
    initialPrevState = null,
    initialPrevCountry = null,
    initialYearsAtAddress = null,
}) => {
    const ZERO = 0;
    const [countryChanges, setCountryChanges] = useState<number>(ZERO);
    const { applicantInfo } = useLocalization();

    const [showPrevAddress, setShowPrevAddress] = useState<boolean>(false);
    const [previousStateOptions, setPreviousStateOptions] = useState<any[]>([]);
    const [showCanadaLabels, setShowCanadaLabels] = useState<boolean>(false);
    const MAXZIPCA = 7;
    const MAXZIPUS = 5;

    const dealerDataInfo = useSelector((state: any) => {
        return state.dealerDataInfo;
    });
    const isCanada =
        dealerDataInfo?.dealer.country === 'Canada' ||
        dealerDataInfo?.dealer.country === 'CA';
    const { markCreditFormTouched } = useCreditForm();
    //for edit mode
    useEffect(() => {
        const MIN_YEARS = 2;
        if (initialYearsAtAddress && initialYearsAtAddress < MIN_YEARS) {
            setShowPrevAddress(true);
        }
    }, []);

    //for edit mode
    useEffect(() => {
        if (initialPrevCountry) {
            const prevCountry =
                initialPrevCountry === 'Canada' || initialPrevCountry === 'CA'
                    ? 'CA'
                    : 'US';
            setPreviousCountry(prevCountry);
        }
    }, []);

    useEffect(() => {
        const LIMIT_COUNTRY_CHANGE = 2;
        const INCREMENT = 1;
        const isCanada = previousCountry == 'CA';
        setPreviousStateOptions(getStateOptions(isCanada));
        setShowCanadaLabels(isCanada);

        if (initialPrevState && countryChanges < LIMIT_COUNTRY_CHANGE) {
            setPreviousState(initialPrevState);
            setCountryChanges(countryChanges + INCREMENT);
            return;
        }

        if (isCanada) {
            setPreviousState('AB');
        } else {
            setPreviousState('AK');
        }
    }, [previousCountry]);

    const checkForPreviousAddress = () => {
        const MINYEARS = 2;
        if (
            values.timeAtAddrYears === '' ||
            Number(values.timeAtAddrYears) >= MINYEARS
        ) {
            setShowPrevAddress(false);
        } else {
            setShowPrevAddress(true);
        }
    };

    const handleInputChange = (e: any) => {
        handleChange(e);
        markCreditFormTouched();
    };

    const validateAddresssLine1 = () => {
        const newError = submitErrors;
        if (values.previousAddressLine1 === '') {
            newError.previousAddressLine1 = applicantInfo.ADDRESS1_ERROR;
        } else {
            newError.previousAddressLine1 = '';
        }
        setSubmitErrors({ ...newError });
    };

    const validateCity = () => {
        const newError = submitErrors;
        if (values.previousCity === '') {
            newError.previousCity = applicantInfo.CITY_ERROR;
        } else {
            newError.previousCity = '';
        }
        setSubmitErrors({ ...newError });
    };

    const validateYears = () => {
        const newError = submitErrors;
        if (values.previousTimeAtAddrYears === '') {
            newError.previousTimeAtAddrYears = applicantInfo.ADRESSYEAR_ERROR;
        } else {
            newError.previousTimeAtAddrYears = '';
        }
        setSubmitErrors({ ...newError });
    };

    const validateMonths = () => {
        const newError = submitErrors;
        if (values.previousTimeAtAddrMonths === '') {
            newError.previousTimeAtAddrMonths = applicantInfo.ADRESSMONTH_ERROR;
        } else {
            newError.previousTimeAtAddrMonths = '';
        }
        setSubmitErrors({ ...newError });
    };

    const validateZip = () => {
        const newError = submitErrors;
        let zipRegex = /^(?!00000)[0-9]{5}$/i;
        let zipError = applicantInfo.ZIP_US_ERROR;
        if (previousCountry == 'CA') {
            zipRegex =
                /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ -]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i;
            zipError = applicantInfo.ZIP_CA_ERROR;
        }

        if (
            values.previousZipOrPostalCode == '' ||
            !values.previousZipOrPostalCode.match(zipRegex)
        ) {
            newError.previousZipOrPostalCode = zipError;
        } else {
            newError.previousZipOrPostalCode = '';
        }
        setSubmitErrors({ ...newError });
    };

    const optionsResidenceType = [
        { value: 'Rent', label: applicantInfo.RESIDENCE_OPTIONRENT },
        { value: 'Own', label: applicantInfo.RESIDENCE_OPTIONOWN },
        {
            value: 'Parents/Guardian',
            label: applicantInfo.RESIDENCE_OPTIONPARENT,
        },
    ];

    const optionsCountries = [
        { value: 'US', label: 'USA' },
        { value: 'CA', label: 'Canada' },
    ];

    const getStateOptions = (isCanada: boolean) => {
        let states = [];
        if (isCanada) {
            states = [
                { value: 'AB', label: applicantInfo.STATE_CA_AB },
                { value: 'BC', label: applicantInfo.STATE_CA_BC },
                { value: 'MB', label: applicantInfo.STATE_CA_MB },
                { value: 'NB', label: applicantInfo.STATE_CA_NB },
                { value: 'NL', label: applicantInfo.STATE_CA_NL },
                { value: 'NS', label: applicantInfo.STATE_CA_NS },
                { value: 'NT', label: applicantInfo.STATE_CA_NT },
                { value: 'NU', label: applicantInfo.STATE_CA_NU },
                { value: 'ON', label: applicantInfo.STATE_CA_ON },
                { value: 'PE', label: applicantInfo.STATE_CA_PE },
                { value: 'QC', label: applicantInfo.STATE_CA_QC },
                { value: 'SK', label: applicantInfo.STATE_CA_SK },
                { value: 'YT', label: applicantInfo.STATE_CA_YT },
            ];
        } else {
            states = [
                { value: 'AK', label: 'AK' },
                { value: 'AL', label: 'AL' },
                { value: 'AR', label: 'AR' },
                { value: 'AZ', label: 'AZ' },
                { value: 'CA', label: 'CA' },
                { value: 'CO', label: 'CO' },
                { value: 'CT', label: 'CT' },
                { value: 'DC', label: 'DC' },
                { value: 'DE', label: 'DE' },
                { value: 'FL', label: 'FL' },
                { value: 'GA', label: 'GA' },
                { value: 'HI', label: 'HI' },
                { value: 'IA', label: 'IA' },
                { value: 'ID', label: 'ID' },
                { value: 'IL', label: 'IL' },
                { value: 'IN', label: 'IN' },
                { value: 'KS', label: 'KS' },
                { value: 'KY', label: 'KY' },
                { value: 'LA', label: 'LA' },
                { value: 'MA', label: 'MA' },
                { value: 'MD', label: 'MD' },
                { value: 'ME', label: 'ME' },
                { value: 'MI', label: 'MI' },
                { value: 'MN', label: 'MN' },
                { value: 'MO', label: 'MO' },
                { value: 'MS', label: 'MS' },
                { value: 'MT', label: 'MT' },
                { value: 'NC', label: 'NC' },
                { value: 'ND', label: 'ND' },
                { value: 'NE', label: 'NE' },
                { value: 'NH', label: 'NH' },
                { value: 'NJ', label: 'NJ' },
                { value: 'NM', label: 'NM' },
                { value: 'NV', label: 'NV' },
                { value: 'NY', label: 'NY' },
                { value: 'OH', label: 'OH' },
                { value: 'OK', label: 'OK' },
                { value: 'OR', label: 'OR' },
                { value: 'PA', label: 'PA' },
                { value: 'RI', label: 'RI' },
                { value: 'SC', label: 'SC' },
                { value: 'SD', label: 'SD' },
                { value: 'TN', label: 'TN' },
                { value: 'TX', label: 'TX' },
                { value: 'UT', label: 'UT' },
                { value: 'VA', label: 'VA' },
                { value: 'VT', label: 'VT' },
                { value: 'WA', label: 'WA' },
                { value: 'WI', label: 'WI' },
                { value: 'WV', label: 'WV' },
                { value: 'WY', label: 'WY' },
            ];
        }
        return states;
    };

    //for edit
    useEffect(() => {
        if (initialState) {
            setState(initialState);
            return;
        }

        if (isCanada) setState('AB');
        else setState('AK');
    }, [isCanada]);

    const handleCurrentZipPostalInput = (event: any) => {
        const { name, value } = event.target;
        let formattedZip = value.trim().toUpperCase();
        if (isCanada) {
            formattedZip = formatPostalCode_CA(formattedZip);
        } else {
            formattedZip = formatPostalCode_US(formattedZip);
        }
        event.target.name = name;
        event.target.value = formattedZip;
        handleInputChange(event);
    };

    const handlePreviousZipChange = (event: any) => {
        const { name, value } = event.target;
        let formattedZip = value.trim().toUpperCase();
        if (formattedZip.length > values.previousZipOrPostalCode.length) {
            if (previousCountry === 'CA') {
                formattedZip = formatPostalCode_CA(formattedZip);
            } else {
                formattedZip = formatPostalCode_US(formattedZip);
            }
        }
        event.target.name = name;
        event.target.value = formattedZip;
        handleInputChange(event);
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line no-magic-numbers
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
        // eslint-disable-next-line no-magic-numbers
        if (raw !== '' && Number(raw) > 11) {
            e.target.value = raw[0];
        } else {
            e.target.value = raw;
        }
        handleInputChange(e);
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line no-magic-numbers
        const cleaned = e.target.value.replace(/\D/g, '').slice(0, 2);
        e.target.value = cleaned;
        handleInputChange(e);
    };

    const handleKeyDownTime = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['e', 'E', '+', '-'].includes(e.key)) {
            e.preventDefault();
        }
    };

    // const FIELD = 'rentOrMortgagePymyt';

    const handleChangeDecimal = (e: React.ChangeEvent<HTMLInputElement>) => {
        // if (e.target.name !== FIELD) return handleInputChange(e);
        let value = e.target.value;
        value = value.replace(/[^\d.]/g, '');
        const parts = value.split('.');

        const int = parts[0] || '';
        const dec = parts[1] || '';

        const formatted =
            // eslint-disable-next-line no-magic-numbers
            int + (value.includes('.') ? '.' + dec.slice(0, 2) : '');
        const num = Number(formatted);
        // eslint-disable-next-line no-magic-numbers
        if (!isNaN(num) && num > 999999.99) return;

        e.target.value = formatted;
        handleInputChange(e);
    };

    const handleBlurDecimal = (e: React.FocusEvent<HTMLInputElement>) => {
        // if (e.target.name !== FIELD) return handleBlur(e);
        let v = (e.target.value || '')
            .trim()
            .replace(/\s+/g, '')
            .replace(',', '.');
        v = v.replace(
            /^(\d+)(?:\.(\d{0,2}))?.*$/,
            (_, a, b = '') => a + (b ? '.' + b : ''),
        );
        e.target.value = v;
        handleBlur(e);
    };

    return (
        <>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-2/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1lh7pdb'
                                className='c-form-field__label'
                            >
                                {applicantInfo.RESIDENCE_LABEL}
                            </label>
                            <div className='c-form-field__box radio-box'>
                                <RadioButton
                                    name={`typeOfResidence${
                                        isCoaplicant ? '2' : ''
                                    }`}
                                    options={optionsResidenceType.map(
                                        (residence) => residence.value,
                                    )}
                                    onSelect={setTypeOfResidence}
                                    selectedOption={typeOfResidence}
                                    labels={optionsResidenceType.map(
                                        (residence) => residence.label,
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    {state == 'QC' && (
                        <div className='o-layout__item  u-1/3  u-1/1@xs'>
                            <div className='c-form-field'>
                                <div className='c-form-field__box'>
                                    <div className='c-input'>
                                        <Input
                                            labelName={
                                                applicantInfo.HOUSING_LABEL
                                            }
                                            type={'text'}
                                            name={'quebeckMonthlyMortgage'}
                                            pattern='\d{1,6}(\.\d{1,2})?'
                                            handlerChange={handleInputChange}
                                            handlerBlur={handleBlurDecimal}
                                            value={
                                                values.quebeckMonthlyMortgage
                                            }
                                            asErrors={
                                                errors.quebeckMonthlyMortgage
                                            }
                                            asTouched={
                                                touched.quebeckMonthlyMortgage
                                            }
                                            asSubmitError={
                                                submitErrors.quebeckMonthlyMortgage
                                            }
                                            prefix='$'
                                            min={0}
                                            maxlength={9}
                                            handleKeyPress={handleKeyDownTime}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={applicantInfo.ADDRESS1_LABEL}
                                        type={'text'}
                                        name={'addressLine1'}
                                        handlerChange={handleInputChange}
                                        handlerBlur={handleBlur}
                                        value={values.addressLine1}
                                        asErrors={errors.addressLine1}
                                        asTouched={touched.addressLine1}
                                        asSubmitError={
                                            submitErrors.addressLine1
                                        }
                                        maxlength={40}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={applicantInfo.ADDRESS2_LABEL}
                                        type={'text'}
                                        name={'addressLine2'}
                                        handlerChange={handleInputChange}
                                        handlerBlur={handleBlur}
                                        value={values.addressLine2}
                                        asErrors={errors.addressLine2}
                                        asTouched={touched.addressLine2}
                                        asSubmitError={
                                            submitErrors.addressLine2
                                        }
                                        maxlength={25}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={applicantInfo.CITY_LABEL}
                                        type={'text'}
                                        name={'city'}
                                        handlerChange={handleInputChange}
                                        handlerBlur={handleBlur}
                                        value={values.city}
                                        asErrors={errors.city}
                                        asTouched={touched.city}
                                        asSubmitError={submitErrors.city}
                                        maxlength={30}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {isCanada
                                    ? applicantInfo.STATE_CA_LABEL
                                    : applicantInfo.STATE_US_LABEL}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'stateSelector'}
                                        options={getStateOptions(isCanada)}
                                        defaultValue={state}
                                        placeholder={
                                            isCanada
                                                ? applicantInfo.STATE_CA_DEFAULT
                                                : applicantInfo.STATE_US_DEFAULT
                                        }
                                        onSelect={(val: any) => {
                                            setState(val);
                                            markCreditFormTouched();
                                        }}
                                        selectedOption={state}
                                        disabled={isDisabled}
                                    />
                                </div>
                                <div
                                    className='custom-input-error'
                                    hidden={stateMistake}
                                >
                                    {applicantInfo.LABEL_FIELD_ERROR_REQUIRED}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={
                                            isCanada
                                                ? applicantInfo.ZIP_CA_LABEL
                                                : applicantInfo.ZIP_US_LABEL
                                        }
                                        type={'text'}
                                        name={'zipOrPostalCode'}
                                        handlerChange={
                                            handleCurrentZipPostalInput
                                        }
                                        handlerBlur={handleBlur}
                                        value={values.zipOrPostalCode}
                                        asErrors={errors.zipOrPostalCode}
                                        asTouched={touched.zipOrPostalCode}
                                        asSubmitError={
                                            submitErrors.zipOrPostalCode
                                        }
                                        maxlength={
                                            isCanada ? MAXZIPCA : MAXZIPUS
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={
                                            applicantInfo.ADRESSYEAR_LABEL
                                        }
                                        type={'number'}
                                        name={'timeAtAddrYears'}
                                        handlerChange={handleYearChange}
                                        handlerBlur={() => {
                                            checkForPreviousAddress();
                                            return handleBlur;
                                        }}
                                        value={values.timeAtAddrYears}
                                        asErrors={errors.timeAtAddrYears}
                                        asTouched={touched.timeAtAddrYears}
                                        asSubmitError={
                                            submitErrors.timeAtAddrYears
                                        }
                                        handleKeyPress={handleKeyDownYearMonth}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <Input
                                        labelName={
                                            applicantInfo.ADRESSMONTH_LABEL
                                        }
                                        type={'number'}
                                        name={'timeAtAddrMonths'}
                                        handlerChange={handleMonthChange}
                                        handlerBlur={handleBlur}
                                        value={values.timeAtAddrMonths}
                                        asErrors={errors.timeAtAddrMonths}
                                        asTouched={touched.timeAtAddrMonths}
                                        asSubmitError={
                                            submitErrors.timeAtAddrMonths
                                        }
                                        handleKeyPress={handleKeyDownYearMonth}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {state != 'QC' && (
                        <div className='o-layout__item  u-1/3  u-1/1@xs'>
                            <div className='c-form-field'>
                                <div className='c-form-field__box'>
                                    <div className='c-input'>
                                        <Input
                                            labelName={applicantInfo.RENT_LABEL}
                                            type={'text'}
                                            name={'rentOrMortgagePymyt'}
                                            pattern='\d{1,6}(\.\d{1,2})?'
                                            handlerChange={handleChangeDecimal}
                                            handlerBlur={handleBlurDecimal}
                                            value={values.rentOrMortgagePymyt}
                                            asErrors={
                                                errors.rentOrMortgagePymyt
                                            }
                                            asTouched={
                                                touched.rentOrMortgagePymyt
                                            }
                                            asSubmitError={
                                                submitErrors.rentOrMortgagePymyt
                                            }
                                            prefix='$'
                                            maxlength={9}
                                            handleKeyPress={handleKeyDownTime}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {state == 'QC' && (
                        <div className='o-layout__item  u-1/3  u-1/1@xs'>
                            <div className='c-form-field'>
                                <div className='c-form-field__box'>
                                    <div className='c-input'>
                                        <Input
                                            labelName={
                                                applicantInfo.MORGAGE_LABEL
                                            }
                                            type={'text'}
                                            name={'quebecMonthlyHoising'}
                                            pattern='\d{1,6}(\.\d{1,2})?'
                                            handlerChange={handleChangeDecimal}
                                            handlerBlur={handleBlurDecimal}
                                            value={values.quebecMonthlyHoising}
                                            asErrors={
                                                errors.quebecMonthlyHoising
                                            }
                                            asTouched={
                                                touched.quebecMonthlyHoising
                                            }
                                            asSubmitError={
                                                submitErrors.quebecMonthlyHoising
                                            }
                                            prefix='$'
                                            min={0}
                                            maxlength={9}
                                            handleKeyPress={handleKeyDownTime}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showPrevAddress && (
                <>
                    <div className='previous-title'>
                        {applicantInfo.PREVIOUS_ADDRESS_TITLE}
                    </div>
                    <div className='previous-subtitle'>
                        {applicantInfo.PREVIOUS_ADDRESS_NOTE}
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1lh7pdb'
                                        className='c-form-field__label'
                                    >
                                        {applicantInfo.RESIDENCE_LABEL}
                                    </label>
                                    <div className='c-form-field__box radio-box'>
                                        <RadioButton
                                            name={`previousTypeOfResidence${
                                                isCoaplicant ? '2' : ''
                                            }`}
                                            options={optionsResidenceType.map(
                                                (residence) => residence.value,
                                            )}
                                            onSelect={(val: any) => {
                                                setPreviousTypeOfResidence(val);
                                                markCreditFormTouched();
                                            }}
                                            selectedOption={
                                                previousTypeOfResidence
                                            }
                                            labels={optionsResidenceType.map(
                                                (residence) => residence.label,
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1lh7pdb'
                                        className='c-form-field__label'
                                    >
                                        {'Country'}
                                    </label>
                                    <div className='c-form-field__box radio-box'>
                                        <RadioButton
                                            name={`previousCountry${
                                                isCoaplicant ? '2' : ''
                                            }`}
                                            options={optionsCountries.map(
                                                (country) => country.value,
                                            )}
                                            onSelect={(val: any) => {
                                                setPreviousCountry(val);
                                                markCreditFormTouched();
                                            }}
                                            selectedOption={previousCountry}
                                            labels={optionsCountries.map(
                                                (country) => country.label,
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    applicantInfo.ADDRESS1_LABEL
                                                }
                                                type={'text'}
                                                name={'previousAddressLine1'}
                                                handlerChange={
                                                    handleInputChange
                                                }
                                                handlerBlur={() => {
                                                    validateAddresssLine1();
                                                    return handleBlur;
                                                }}
                                                value={
                                                    values.previousAddressLine1
                                                }
                                                asErrors={
                                                    errors.previousAddressLine1
                                                }
                                                asTouched={
                                                    touched.previousAddressLine1
                                                }
                                                asSubmitError={
                                                    submitErrors.previousAddressLine1
                                                }
                                                maxlength={40}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    applicantInfo.ADDRESS2_LABEL
                                                }
                                                type={'text'}
                                                name={'previousAddressLine2'}
                                                handlerChange={
                                                    handleInputChange
                                                }
                                                handlerBlur={handleBlur}
                                                value={
                                                    values.previousAddressLine2
                                                }
                                                asErrors={
                                                    errors.previousAddressLine2
                                                }
                                                asTouched={
                                                    touched.previousAddressLine2
                                                }
                                                asSubmitError={
                                                    submitErrors.previousAddressLine2
                                                }
                                                maxlength={40}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    applicantInfo.CITY_LABEL
                                                }
                                                type={'text'}
                                                name={'previousCity'}
                                                handlerChange={
                                                    handleInputChange
                                                }
                                                handlerBlur={() => {
                                                    validateCity();
                                                    return handleBlur;
                                                }}
                                                value={values.previousCity}
                                                asErrors={errors.previousCity}
                                                asTouched={touched.previousCity}
                                                asSubmitError={
                                                    submitErrors.previousCity
                                                }
                                                maxlength={30}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {showCanadaLabels
                                            ? applicantInfo.STATE_CA_LABEL
                                            : applicantInfo.STATE_US_LABEL}
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <ComboBox
                                                name={'previousStateSelector'}
                                                options={previousStateOptions}
                                                defaultValue={previousState}
                                                placeholder={
                                                    showCanadaLabels
                                                        ? applicantInfo.STATE_CA_DEFAULT
                                                        : applicantInfo.STATE_US_DEFAULT
                                                }
                                                onSelect={(val: any) => {
                                                    setPreviousState(val);
                                                    markCreditFormTouched();
                                                }}
                                                selectedOption={previousState}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                        <div
                                            className='custom-input-error'
                                            hidden={previousStateMistake}
                                        >
                                            {
                                                applicantInfo.LABEL_FIELD_ERROR_REQUIRED
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    showCanadaLabels
                                                        ? applicantInfo.ZIP_CA_LABEL
                                                        : applicantInfo.ZIP_US_LABEL
                                                }
                                                type={'text'}
                                                name={'previousZipOrPostalCode'}
                                                handlerChange={
                                                    handlePreviousZipChange
                                                }
                                                handlerBlur={() => {
                                                    validateZip();
                                                    return handleBlur;
                                                }}
                                                value={
                                                    values.previousZipOrPostalCode
                                                }
                                                asErrors={
                                                    errors.previousZipOrPostalCode
                                                }
                                                asTouched={
                                                    touched.previousZipOrPostalCode
                                                }
                                                asSubmitError={
                                                    submitErrors.previousZipOrPostalCode
                                                }
                                                maxlength={
                                                    previousCountry === 'CA'
                                                        ? MAXZIPCA
                                                        : MAXZIPUS
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    applicantInfo.ADRESSYEAR_LABEL
                                                }
                                                type={'number'}
                                                name={'previousTimeAtAddrYears'}
                                                handlerChange={handleYearChange}
                                                handlerBlur={() => {
                                                    validateYears();
                                                    return handleBlur;
                                                }}
                                                value={
                                                    values.previousTimeAtAddrYears
                                                }
                                                asErrors={
                                                    errors.previousTimeAtAddrYears
                                                }
                                                asTouched={
                                                    touched.previousTimeAtAddrYears
                                                }
                                                asSubmitError={
                                                    submitErrors.previousTimeAtAddrYears
                                                }
                                                handleKeyPress={
                                                    handleKeyDownYearMonth
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <Input
                                                labelName={
                                                    applicantInfo.ADRESSMONTH_LABEL
                                                }
                                                type={'number'}
                                                name={
                                                    'previousTimeAtAddrMonths'
                                                }
                                                handlerChange={
                                                    handleMonthChange
                                                }
                                                handlerBlur={() => {
                                                    validateMonths();
                                                    return handleBlur;
                                                }}
                                                value={
                                                    values.previousTimeAtAddrMonths
                                                }
                                                asErrors={
                                                    errors.previousTimeAtAddrMonths
                                                }
                                                asTouched={
                                                    touched.previousTimeAtAddrMonths
                                                }
                                                asSubmitError={
                                                    submitErrors.previousTimeAtAddrMonths
                                                }
                                                handleKeyPress={
                                                    handleKeyDownYearMonth
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ResidenceForm;
